/**
 * Window ID Management System
 *
 * 각 브라우저 창/탭마다 고유한 ID를 부여하여 독립적인 상태 관리를 가능하게 합니다.
 * sessionStorage를 사용하므로 새로고침 시에도 같은 ID를 유지하지만,
 * 창을 닫으면 자동으로 정리됩니다.
 */

import { safeStorage } from './safeStorage';

const WINDOW_ID_KEY = '__window_id__';

/**
 * UUID v4 생성 (모던 브라우저)
 * fallback으로 timestamp + random 조합 사용
 */
function generateUUID(): string {
  if (typeof window === 'undefined') {
    return 'server-' + Date.now() + '-' + Math.random().toString(36).substring(2, 15);
  }

  // crypto.randomUUID() 사용 (모던 브라우저)
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    try {
      return crypto.randomUUID();
    } catch (error) {
      console.warn('[windowId] crypto.randomUUID() failed, using fallback:', error);
    }
  }

  // Fallback: timestamp + random
  return 'win-' + Date.now() + '-' + Math.random().toString(36).substring(2, 15);
}

/**
 * 현재 창의 고유 ID를 가져옵니다.
 * sessionStorage에 저장되어 있으면 재사용하고, 없으면 새로 생성합니다.
 *
 * @returns 현재 창의 고유 ID
 *
 * @example
 * ```typescript
 * const windowId = getWindowId();
 * // 'win-1234567890-abc123def456'
 * ```
 */
export function getWindowId(): string {
  if (typeof window === 'undefined') {
    return 'server-default';
  }

  try {
    // sessionStorage에서 기존 ID 확인
    let windowId = safeStorage.getItem(WINDOW_ID_KEY, 'session');

    if (!windowId) {
      // 없으면 새로 생성
      windowId = generateUUID();
      safeStorage.setItem(WINDOW_ID_KEY, windowId, 'session');
      console.log('[windowId] New window ID created:', windowId);
    }

    return windowId;
  } catch (error) {
    console.error('[windowId] Failed to get/set window ID:', error);
    // sessionStorage 접근 실패 시 메모리 기반 fallback
    return 'fallback-' + Date.now();
  }
}

/**
 * 현재 창의 ID를 명시적으로 재설정합니다.
 * 주로 테스트나 디버깅 목적으로 사용됩니다.
 *
 * @param newId - 새로운 Window ID (선택적, 없으면 자동 생성)
 *
 * @example
 * ```typescript
 * resetWindowId(); // 새 ID 자동 생성
 * resetWindowId('test-window-1'); // 특정 ID 설정
 * ```
 */
export function resetWindowId(newId?: string): string {
  if (typeof window === 'undefined') {
    return 'server-default';
  }

  try {
    const windowId = newId || generateUUID();
    safeStorage.setItem(WINDOW_ID_KEY, windowId, 'session');
    console.log('[windowId] Window ID reset to:', windowId);
    return windowId;
  } catch (error) {
    console.error('[windowId] Failed to reset window ID:', error);
    return 'fallback-' + Date.now();
  }
}
/**
 * Window ID를 포함한 Storage 키를 생성합니다.
 *
 * @param baseKey - 기본 키
 * @returns Window ID가 포함된 키
 *
 * @example
 * ```typescript
 * const key = getWindowScopedKey('tab-paths-v1');
 * // 'win-1234567890-abc123def456:tab-paths-v1'
 * ```
 */
export function getWindowScopedKey(baseKey: string): string {
  const windowId = getWindowId();
  return `${windowId}:${baseKey}`;
}
